import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  map,
  switchMap
} from 'rxjs/operators';
import { formatErrorCode } from '../../adapters/utils/utils.adapter';
import { BOOK_WIDGET_ERROR_CODES } from '../../constants/error-codes.constants';

import { PredictiveSearchService } from '../../services/predictive-search.service';
import * as fromBookWidgetActions from '../actions/book-widget.actions';
import * as fromErrorHandlerActions from '../actions/error-handler.actions';
import * as fromPredictiveSearchAction from '../actions/predictive-search.actions';
import { predictiveSearchApiFailed } from '../selectors/predictive-search.selectors';

@Injectable({ providedIn: 'root' })
export class PredictiveSearchEffects {
  loadPredictiveSearch$ = createEffect(
    (): Observable<Action> =>
      this.PredictiveSearchActions$.pipe(
        ofType(fromBookWidgetActions.InitPredictiveSearch),
        concatLatestFrom(() => this.store.select(predictiveSearchApiFailed)),
        distinctUntilChanged(
          ([prev], [curr, apiFailed]) =>
            !apiFailed &&
            prev.predictiveSearchString.slice(0, 3) ===
              curr.predictiveSearchString.slice(0, 3)
        ),
        switchMap(([{ predictiveSearchString }]) =>
          of(
            fromPredictiveSearchAction.LoadPredictiveSearchApi({
              predictiveSearchString
            })
          )
        )
      )
  );

  LoadPredictiveSearchApi$ = createEffect(
    (): Observable<Action> =>
      this.PredictiveSearchActions$.pipe(
        ofType(fromPredictiveSearchAction.LoadPredictiveSearchApi),
        switchMap(({ predictiveSearchString }) =>
          this.service
            .getPredictiveSearch(predictiveSearchString.slice(0, 3))
            .pipe(
              map((predictiveSearchResponse) => {
                return fromPredictiveSearchAction.LoadPredictiveSearchApiSuccess(
                  {
                    predictiveSearchResponse
                  }
                );
              }),
              catchError((error: HttpErrorResponse) =>
                of(
                  fromErrorHandlerActions.HandleError({
                    errorCode: formatErrorCode(
                      BOOK_WIDGET_ERROR_CODES.PREDICTIVE_SEARCH_AWS_BFF_API,
                      error?.status
                    ),
                    error: BOOK_WIDGET_ERROR_CODES.PREDICTIVE_SEARCH_AWS_BFF_API
                  }),
                  fromPredictiveSearchAction.LoadPredictiveSearchApiFailure()
                )
              )
            )
        )
      )
  );

  constructor(
    private PredictiveSearchActions$: Actions,
    private service: PredictiveSearchService,
    private store: Store
  ) {}
}
